import * as React from "react";
import classnames from "classnames";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { RouteComponentProps, withRouter } from "react-router";
import "moment-timezone";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as CurrencyFormat from "react-currency-format";
import {
  Input,
  Modal,
  Button,
  Collapse,
  FormGroup,
  ModalBody,
  ButtonGroup,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

import { RootState } from "../../store/rootReducer";
import { Accident } from "../../lib/interfaces/Accident.interface";
import { Employee } from "../../lib/interfaces/Employee.interface";
import { Location } from "../../lib/interfaces/Location.interface";
import { AccidentComment } from "../../lib/interfaces/AccidentComment.interface";
import {
  User,
  hasPermission,
  hasAnyPermission,
} from "../../lib/interfaces/User.interface";

import FileList from "src/common/FileList.component";
import FileDropzone from "src/common/FileDropzone.component";
import EntityCommentList from "src/common/EntityCommentList.component";
import ClaimsList, {
  ClaimsListViewMode,
  ClaimsListViewModeToggler,
} from "src/common/ClaimsList.component";

import Breadcrumbs from "../Breadcrumbs.component";
import LocationImage from "../../common/LocationImage.component";

import * as appSelectors from "../../store/app/selectors";
import * as accidentsActions from "../../store/accidents/actions";
import * as employeesActions from "../../store/employees/actions";

import { getManagerApprovalString } from "src/lib/helpers/managerApproval";

const moment = require("moment");

const get = require("lodash/get");
const map = require("lodash/map");
const sortBy = require("lodash/sortBy");
const isEmpty = require("lodash/isEmpty");

interface RouteInfo {
  accidentId: string;
}

export interface AccidentDetailsProps extends RouteComponentProps<RouteInfo> {
  user: User;
  accident?: Accident;
  fetchFilesError?: any;
  isDeletingClaim: boolean;
  isFetchingFiles: boolean;
  isUploadingFile: boolean;
  isPostingComment: boolean;
  isDeletingAccident: boolean;
  resetCurrentAccident: () => void;
  fetchAccidentFiles: (accidentId: number) => void;
  setCurrentEmployee: (employee: Employee) => void;
  removeClaim: (claimId: number, next?: () => void) => void;
  uploadAccidentFile: (accidentId: string, file: any) => void;
  getLocationWithId: (locationId: number) => Location | undefined;
  postAccidentComment: (accidentId: string, comment: string) => void;
  removeAccident: (
    accidentId: number | string,
    next?: (success: boolean) => void
  ) => void;
  fetchAccident: (
    accidentId: string,
    next?: (error?: any, accident?: Accident) => void
  ) => void;
  setAccidentManagerApproval: (
    accidentId: number | string,
    managerApproval: string,
    next?: () => void
  ) => void;
}

export interface AccidentDetailsState {
  comment: string;
  claimDeletionId?: number;
  settingManagerApproval?: string;
  accidentDeletionModalOpen: boolean;
  claimsListViewMode: ClaimsListViewMode;
}

class AccidentDetails extends React.PureComponent<
  AccidentDetailsProps,
  AccidentDetailsState
> {
  state: AccidentDetailsState = {
    comment: "",
    claimsListViewMode: "table",
    accidentDeletionModalOpen: false,
  };

  get accidentId() {
    return this.props.match.params.accidentId;
  }

  componentDidMount() {
    const { resetCurrentAccident } = this.props;

    resetCurrentAccident();
    this.fetchAccident();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.accidentId !== prevProps.match.params.accidentId) {
      this.fetchAccident();
    }
  }

  fetchAccident() {
    this.props.fetchAccident(
      this.accidentId,
      (error?: any, accident?: Accident) => {
        if (!error) {
          this.fetchFiles();
        }
      }
    );
  }

  fetchFiles = () => {
    this.props.fetchAccidentFiles(parseInt(this.accidentId, 10));
  };

  handleCommentSubmit = (e) => {
    e.preventDefault();

    const { comment } = this.state;
    const { postAccidentComment } = this.props;

    this.setState({ comment: "" }, () =>
      postAccidentComment(this.accidentId, comment)
    );
  };

  handleFileUpload = (file) => {
    const { uploadAccidentFile } = this.props;
    uploadAccidentFile(this.accidentId, file);
  };

  renderManagerApprovalButton(approval, title) {
    const { accident } = this.props;
    const { settingManagerApproval } = this.state;

    const isManagerApprovalSelected = (managerApproval) =>
      accident && accident.managerApproval === managerApproval;

    const onClick = () => {
      const { setAccidentManagerApproval } = this.props;

      this.setState({ settingManagerApproval: approval }, () => {
        setAccidentManagerApproval(this.accidentId, approval, () => {
          this.setState({ settingManagerApproval: undefined });
        });
      });
    };

    return (
      <Button
        onClick={onClick}
        className="ggbutton"
        active={isManagerApprovalSelected(approval)}
        disabled={settingManagerApproval === approval}
        style={{ paddingLeft: "10px", paddingRight: "10px" }}
        color={isManagerApprovalSelected(approval) ? "primary" : "light"}
      >
        {settingManagerApproval === approval && (
          <FontAwesomeIcon spin className="mr-2" icon={["fas", "spinner"]} />
        )}
        {title}
      </Button>
    );
  }

  renderEmployee(employee?: Employee, defaultValue?: string) {
    if (!employee) {
      return defaultValue;
    }

    const { setCurrentEmployee } = this.props;
    return (
      <a
        className="cursor-pointer"
        onClick={() => setCurrentEmployee(employee!)}
      >
        <u>{`${employee!.firstName || ""} ${employee!.lastName || ""}`}</u>
      </a>
    );
  }

  renderCurrencyValue(value) {
    return (
      <CurrencyFormat
        prefix="$"
        value={value}
        decimalScale={2}
        displayType="text"
        thousandSeparator={true}
        fixedDecimalScale={true}
      />
    );
  }

  get accidentLocationCode() {
    const { accident } = this.props;
    return get(
      accident,
      "locationPosition.location.code",
      get(accident, "location.code")
    );
  }

  handleDeleteClaim(claimId) {
    const { removeClaim, fetchAccident } = this.props;

    removeClaim(claimId, () => {
      this.setState({ claimDeletionId: undefined }, () =>
        fetchAccident(this.accidentId)
      );
    });
  }

  handleDeleteAccident(accidentId) {
    const { history, removeAccident } = this.props;

    removeAccident(accidentId, (success: boolean) => {
      if (success) {
        this.setState({ accidentDeletionModalOpen: false }, () =>
          history.push("/accidents")
        );
      }
    });
  }

  renderClaimDeletionConfirmationModal() {
    const { claimDeletionId } = this.state;
    const { isDeletingClaim } = this.props;

    return (
      <Modal isOpen={!!claimDeletionId}>
        <ModalHeader
          toggle={() => this.setState({ claimDeletionId: undefined })}
        >
          Delete claim
        </ModalHeader>
        <ModalBody>Are you sure you want to delete this claim?</ModalBody>
        <ModalFooter>
          <Button
            color="light"
            className="ggbutton"
            onClick={() => this.setState({ claimDeletionId: undefined })}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={isDeletingClaim}
            className="ggbutton"
            onClick={() => this.handleDeleteClaim(claimDeletionId)}
          >
            {isDeletingClaim && (
              <FontAwesomeIcon
                spin
                className="mr-3"
                icon={["fas", "spinner"]}
              />
            )}
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderAccidentDeletionConfirmationModal() {
    const { accidentDeletionModalOpen } = this.state;
    const { accident, isDeletingAccident } = this.props;

    const claimsCount = get(accident, "claims.length", 0);

    return (
      <Modal isOpen={accidentDeletionModalOpen}>
        <ModalHeader
          toggle={() => this.setState({ accidentDeletionModalOpen: false })}
        >
          Delete accident
        </ModalHeader>
        <ModalBody>
          {claimsCount === 0
            ? "Are you sure you want to delete this accident?"
            : "Please remove all claims before deleting the accident."}
        </ModalBody>
        <ModalFooter>
          {claimsCount > 0 && (
            <Button
              color="primary"
              className="ggbutton"
              onClick={() =>
                this.setState({ accidentDeletionModalOpen: false })
              }
            >
              Ok
            </Button>
          )}
          {claimsCount === 0 && [
            <Button
              key="cancel"
              color="light"
              className="ggbutton"
              onClick={() =>
                this.setState({ accidentDeletionModalOpen: false })
              }
            >
              Cancel
            </Button>,
            <Button
              key="delete"
              color="primary"
              disabled={isDeletingAccident}
              className="ggbutton"
              onClick={() => this.handleDeleteAccident(this.accidentId)}
            >
              {isDeletingAccident && (
                <FontAwesomeIcon
                  spin
                  className="mr-3"
                  icon={["fas", "spinner"]}
                />
              )}
              Delete
            </Button>,
          ]}
        </ModalFooter>
      </Modal>
    );
  }

  renderAccidentDate(dateFormat) {
    const { accident } = this.props;

    const lossDate = get(accident, "lossDate");
    if (lossDate) {
      return moment(lossDate).format(dateFormat);
    }

    return "-";
  }

  render() {
    const { claimsListViewMode } = this.state;
    const {
      user,
      accident,
      fetchFilesError,
      isFetchingFiles,
      isPostingComment,
      isUploadingFile,
      isDeletingAccident,
    } = this.props;

    const accidentLocationCode = this.accidentLocationCode;

    return (
      <div>
        <Breadcrumbs
          breadcrumbs={[
            { name: "Accidents", link: "/accidents" },
            { name: `ERAC ${this.accidentId}`, active: true },
          ]}
        />

        {accident && (
          <div className="accident-page mt-4">
            {this.renderClaimDeletionConfirmationModal()}
            {this.renderAccidentDeletionConfirmationModal()}
            <section className="container-fluid pl-0 pr-0 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">Accident Status & Details</h5>
                {hasPermission(user, "ROLE_ACCIDENT_ADMIN") && (
                  <React.Fragment>
                    <Link to={`/accidents/${this.accidentId}/edit`}>
                      <Button size="sm" color="dark" className="ggbutton ml-3">
                        Edit
                      </Button>
                    </Link>

                    <Button
                      size="sm"
                      color="light"
                      className="ggbutton ml-2"
                      disabled={isDeletingAccident}
                      onClick={() =>
                        this.setState({ accidentDeletionModalOpen: true })
                      }
                    >
                      {isDeletingAccident && (
                        <FontAwesomeIcon
                          spin
                          className="mr-3"
                          icon={["fas", "spinner"]}
                        />
                      )}
                      Delete
                    </Button>
                  </React.Fragment>
                )}
              </div>

              <div className="row no-gutters mt-4">
                <div className="col-12 col-md-8 col-lg-4 pr-md-3">
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td>Accident date:</td>
                          <td>{this.renderAccidentDate("MMM DD[,] YYYY")}</td>
                        </tr>
                        <tr>
                          <td>Accident time:</td>
                          <td>{this.renderAccidentDate("hh:mm:ss A")}</td>
                        </tr>
                        <tr>
                          <td>Manager approval:</td>
                          <td>
                            {getManagerApprovalString(
                              accident && accident.managerApproval
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {accidentLocationCode && (
                  <div className="col-12 col-md-4 col-lg-2 pr-lg-3 mt-3 mt-md-0">
                    <div
                      className="ggcard h-100"
                      style={{ minHeight: "125px" }}
                    >
                      <LocationImage locationCode={accidentLocationCode} />
                    </div>
                  </div>
                )}

                <div className="col-12 col-sm-5 col-lg-3 pr-0 pr-sm-3 mt-3 mt-lg-0">
                  <div className="ggcard h-100 p-3">
                    <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center">
                      <span className="costlabel">
                        {this.renderCurrencyValue(accident.totalCost)}
                      </span>
                      <span>Total Damage</span>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-7 col-lg-3 mt-3 mt-lg-0">
                  <div className="ggcard h-100 p-2">
                    <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center pt-1">
                      <span>
                        <b className="text-uppercase">Manager approval</b>
                      </span>
                      <span
                        className="text-primary text-medium"
                        style={{ fontSize: "18px" }}
                      >
                        <b>
                          {getManagerApprovalString(accident.managerApproval)}
                        </b>
                      </span>
                      {hasAnyPermission(user, [
                        "ROLE_ACCIDENT_ADMIN",
                        "ROLE_CITY_MANAGER",
                      ]) && (
                        <ButtonGroup className="mt-2">
                          {this.renderManagerApprovalButton(
                            "APPROVED",
                            "Approve"
                          )}
                          {this.renderManagerApprovalButton(
                            "DISPUTING",
                            "Dispute"
                          )}
                          {this.renderManagerApprovalButton("DENIED", "Deny")}
                        </ButtonGroup>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 col-md-6 pr-3 pr-0 pr-md-3">
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        {/* <tr>
                          <td>Driver:</td>
                          <td>
                            {this.renderEmployee(accident.leadDriver, 'None')}
                          </td>
                        </tr> */}
                        <tr>
                          <td>Position:</td>
                          <td>
                            {get(accident, "locationPosition.name", "None")}
                          </td>
                        </tr>
                        <tr>
                          <td>Created at:</td>
                          <td>
                            {moment(accident.dateCreated).format(
                              "MM/DD/YYYY hh:mm A"
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>

            <section className="container-fluid pl-0 pr-0 mt-5 ml-0">
              <h5 className="title">Accident Updates</h5>

              <div className="w-100 mt-3">
                <div className="d-flex flex-column">
                  <EntityCommentList comments={accident.comments} />

                  <div className="ggcard p-3 mt-3">
                    <span className="text-uppercase">
                      <b>Add an update:</b>
                    </span>
                    <FormGroup className="mt-3 mb-0">
                      <Input
                        type="textarea"
                        value={this.state.comment}
                        onChange={(e) =>
                          this.setState({ comment: e.target.value })
                        }
                      />

                      <Button
                        type="submit"
                        color="primary"
                        className="btn w-100 mt-4"
                        onClick={this.handleCommentSubmit}
                        disabled={
                          isPostingComment || isEmpty(this.state.comment)
                        }
                      >
                        {isPostingComment && (
                          <FontAwesomeIcon
                            spin
                            className="mr-3"
                            icon={["fas", "spinner"]}
                          />
                        )}
                        Submit
                      </Button>
                    </FormGroup>
                  </div>
                </div>
              </div>
            </section>

            <section className="pl-0 mt-5 mb-4 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">Claims Against This Accident</h5>
                <ClaimsListViewModeToggler
                  className="ml-2"
                  viewMode={claimsListViewMode}
                  onChange={(viewMode) =>
                    this.setState({ claimsListViewMode: viewMode })
                  }
                />
                {hasPermission(user, "ROLE_ACCIDENT_ADMIN") && (
                  <Link to={`/accidents/${this.accidentId}/claims/add`}>
                    <Button className="ggbutton btn-sm btn-dark ml-3">
                      Add
                    </Button>
                  </Link>
                )}
              </div>

              <ClaimsList
                className="mt-2"
                showDetailsOnClick
                claims={accident.claims}
                viewMode={claimsListViewMode}
                emptyMessage="There are no claims attached to this accident."
                onRowClick={() => this.setState({ claimsListViewMode: "card" })}
                renderFooter={(claim) =>
                  hasPermission(user, "ROLE_ACCIDENT_ADMIN") && (
                    <div className="row no-gutters">
                      <div className="col-6 pr-1">
                        <Button
                          color="light"
                          className="ggbutton w-100"
                          onClick={() =>
                            this.setState({ claimDeletionId: claim.id })
                          }
                        >
                          Delete this claim
                        </Button>
                      </div>

                      <div className="col-6 pl-1">
                        <Link
                          to={`/accidents/${this.accidentId}/claims/${claim.id}`}
                        >
                          <Button color="primary" className="ggbutton w-100">
                            Edit this claim
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )
                }
              />
            </section>

            <section className="pl-0 mt-5 mb-4 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">Files Linked to This Accident</h5>
                {isFetchingFiles && (
                  <FontAwesomeIcon
                    spin
                    className="ml-2"
                    icon={["fas", "spinner"]}
                    style={{ fontSize: "20px" }}
                  />
                )}
              </div>

              <div className="mt-4">
                <FileList
                  entity="accident"
                  files={accident.files}
                  entityId={accident.id}
                  error={fetchFilesError}
                  onDelete={this.fetchFiles}
                  className="row no-gutters"
                  onTryAgain={this.fetchFiles}
                  fileWrapperClassName="col-4 pr-3 pb-3"
                  canDelete={hasPermission(user, "ROLE_ACCIDENT_ADMIN")}
                />

                <div className="w-100 mt-4">
                  <FileDropzone
                    isLoading={isUploadingFile}
                    onDrop={this.handleFileUpload}
                  />
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  accident: state.accidents.accidentDetails,
  fetchFilesError: state.accidents.fetchFilesError,
  isDeletingClaim: state.accidents.isDeletingClaim,
  isFetchingFiles: state.accidents.isFetchingFiles,
  isUploadingFile: state.accidents.isUploadingFile,
  isPostingComment: state.accidents.isPostingComment,
  isDeletingAccident: state.accidents.isDeletingAccident,
  getLocationWithId: appSelectors.getLocationWithId(state),
});

const mapDispatchToProps = (dispatch) => ({
  resetCurrentAccident: () =>
    dispatch(accidentsActions.setCurrentAccident(undefined)),
  setCurrentEmployee: (employee: Employee) =>
    dispatch(employeesActions.setCurrentEmployee(employee)),
  fetchAccidentFiles: (accidentId: number) =>
    dispatch(accidentsActions.fetchAccidentFiles(accidentId)),
  removeClaim: (claimId: number, next?: () => void) =>
    dispatch(accidentsActions.removeClaim(claimId, next)),
  uploadAccidentFile: (accidentId: string, file: any) =>
    dispatch(accidentsActions.uploadAccidentFile(accidentId, file)),
  postAccidentComment: (accidentId: string, comment: string) =>
    dispatch(accidentsActions.postAccidentComment(accidentId, comment)),
  removeAccident: (
    accidentId: number | string,
    next?: (success: boolean) => void
  ) => dispatch(accidentsActions.removeAccident(accidentId, next)),
  fetchAccident: (
    accidentId: string,
    next?: (error?: any, accident?: Accident) => void
  ) => dispatch(accidentsActions.fetchAccident(accidentId, next)),
  setAccidentManagerApproval: (
    accidentId: number | string,
    managerApproval: string,
    next?: () => void
  ) =>
    dispatch(
      accidentsActions.setAccidentManagerApproval(
        accidentId,
        managerApproval,
        next
      )
    ),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AccidentDetails)
);



// WEBPACK FOOTER //
// ./src/routes/accidents/AccidentDetails.page.tsx